import { CollectionMethod } from "@redotech/redo-billing-common/billing-engine-types";
import {
  cadenceAnchorsOnDayOfMonth,
  MAX_DAY_OF_MONTH,
  MAX_NET_DAYS,
  MIN_DAY_OF_MONTH,
  type NetTermsSettings as NetTermsSettingsModel,
  validateNetTermsSettings,
} from "@redotech/redo-billing-common/net-terms";
import { BillingPeriod } from "@redotech/redo-billing-common/service-invoice";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { titleCase } from "@redotech/web-util/strings";
import { memo, useMemo } from "react";

export const netTermsSettingsForm = groupInput({
  enabled: input<boolean>(),
  cadence: input<BillingPeriod>(),
  dayOfWeek: input<number>(),
  dayOfMonth: input<number>(),
  collectionMethod: input<CollectionMethod>(),
  netTermsDays: input<number>(),
});

export type NetTermsSettingsForm = InputSchema.Input<
  typeof netTermsSettingsForm
>;

export type NetTermsSettingsValue = InputSchema.Value<
  typeof netTermsSettingsForm
>;

export const netTermsSettingsDefaultValue: NetTermsSettingsValue = {
  enabled: false,
  cadence: BillingPeriod.MONTHLY,
  dayOfWeek: 1,
  dayOfMonth: 1,
  collectionMethod: CollectionMethod.AUTO,
  netTermsDays: 7,
};

/**
 * The stored shape leaves the day fields unset for cadences that do not use
 * them; the form always holds a concrete number, so fall back to the defaults.
 */
export function netTermsValueFromSettings(
  netTerms: NetTermsSettingsModel | undefined,
): NetTermsSettingsValue {
  return {
    enabled: netTerms?.enabled ?? netTermsSettingsDefaultValue.enabled,
    cadence: netTerms?.cadence ?? netTermsSettingsDefaultValue.cadence,
    dayOfWeek: netTerms?.dayOfWeek ?? netTermsSettingsDefaultValue.dayOfWeek,
    dayOfMonth: netTerms?.dayOfMonth ?? netTermsSettingsDefaultValue.dayOfMonth,
    collectionMethod:
      netTerms?.collectionMethod ??
      netTermsSettingsDefaultValue.collectionMethod,
    netTermsDays:
      netTerms?.netTermsDays ?? netTermsSettingsDefaultValue.netTermsDays,
  };
}

const cadenceLabels: Record<BillingPeriod, string> = {
  [BillingPeriod.WEEKLY]: "Weekly",
  [BillingPeriod.BIMONTHLY]: "Twice a month",
  [BillingPeriod.MONTHLY]: "Monthly",
  [BillingPeriod.YEARLY]: "Yearly",
};

const cadenceOptions: RedoListItem<BillingPeriod>[] = Object.values(
  BillingPeriod,
).map((cadence) => ({
  id: cadence,
  type: "text" as const,
  text: cadenceLabels[cadence],
  value: cadence,
}));

const collectionMethodOptions: RedoListItem<CollectionMethod>[] = [
  {
    id: CollectionMethod.AUTO,
    type: "text" as const,
    text: "Charge automatically when due",
    value: CollectionMethod.AUTO,
  },
  {
    id: CollectionMethod.MANUAL,
    type: "text" as const,
    text: "Email the merchant to pay",
    value: CollectionMethod.MANUAL,
  },
];

const dayOfWeekNames = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const dayOfWeekOptions: RedoListItem<number>[] = dayOfWeekNames.map(
  (name, index) => ({
    id: name,
    type: "text" as const,
    text: titleCase(name),
    value: index,
  }),
);

export const NetTermsSettings = memo(function NetTermsSettings({
  netTermsInput,
}: {
  netTermsInput: NetTermsSettingsForm;
}) {
  const {
    enabled,
    cadence,
    dayOfWeek,
    dayOfMonth,
    collectionMethod,
    netTermsDays,
  } = netTermsInput.inputs;

  const validationError = useMemo(
    () =>
      validateNetTermsSettings({
        enabled: enabled.value,
        cadence: cadence.value,
        dayOfWeek: dayOfWeek.value,
        dayOfMonth: dayOfMonth.value,
        collectionMethod: collectionMethod.value,
        netTermsDays: netTermsDays.value,
      }),
    [
      enabled.value,
      cadence.value,
      dayOfWeek.value,
      dayOfMonth.value,
      collectionMethod.value,
      netTermsDays.value,
    ],
  );

  return (
    <Card title="Net terms billing">
      <Flex flexDirection="column">
        <RedoToggle
          description="Invoice accumulated label charges on a cadence and collect when due, instead of charging each label against the balance. The debt cap stays the minimum balance above."
          label="Enable net terms"
          setValue={(value) => enabled.setValue(value)}
          value={enabled.value}
        />

        {enabled.value && (
          <>
            <Flex align="center" flexDirection="row">
              <Text>Billing cadence</Text>
              <RedoSingleSelectDropdownInput<BillingPeriod>
                options={cadenceOptions}
                optionSelected={({ item }) => cadence.setValue(item.value)}
                selectedItem={cadenceOptions.find(
                  (option) => option.value === cadence.value,
                )}
                size="xs"
              />
            </Flex>

            {!cadenceAnchorsOnDayOfMonth(cadence.value) && (
              <Flex align="center" flexDirection="row">
                <Text>Invoice on</Text>
                <RedoSingleSelectDropdownInput<number>
                  options={dayOfWeekOptions}
                  optionSelected={({ item }) => dayOfWeek.setValue(item.value)}
                  selectedItem={dayOfWeekOptions.find(
                    (option) => option.value === dayOfWeek.value,
                  )}
                  size="xs"
                />
              </Flex>
            )}

            {cadenceAnchorsOnDayOfMonth(cadence.value) && (
              <Flex align="center" flexDirection="row">
                <Text>Invoice on day of month</Text>
                <RedoIncrementDecrement
                  max={MAX_DAY_OF_MONTH}
                  min={MIN_DAY_OF_MONTH}
                  placeholder="1"
                  setValue={(value) => dayOfMonth.setValue(value ?? 1)}
                  size="small"
                  value={dayOfMonth.value ?? 1}
                />
              </Flex>
            )}

            <Flex align="center" flexDirection="row">
              <Text>Collection method</Text>
              <RedoSingleSelectDropdownInput<CollectionMethod>
                options={collectionMethodOptions}
                optionSelected={({ item }) =>
                  collectionMethod.setValue(item.value)
                }
                selectedItem={collectionMethodOptions.find(
                  (option) => option.value === collectionMethod.value,
                )}
                size="xs"
              />
            </Flex>

            <Flex align="center" flexDirection="row">
              <Text>Net period (days)</Text>
              <RedoIncrementDecrement
                max={MAX_NET_DAYS}
                min={1}
                placeholder="7"
                setValue={(value) => netTermsDays.setValue(value ?? 1)}
                size="small"
                value={netTermsDays.value ?? 1}
              />
            </Flex>
            <Text fontSize="sm" textColor="secondary">
              Cadence and net period are independent — weekly billing on net 7
              bills every week with each invoice due 7 days after it is issued.
              Yearly repeats in the month the cycle started.
            </Text>

            {validationError && (
              <Text fontSize="sm" textColor="error">
                {validationError}
              </Text>
            )}
          </>
        )}
      </Flex>
    </Card>
  );
});
